// Stripe API service for admin dashboard
// All endpoints are protected by admin JWT (cookie or Authorization header)

import stripeApiClient from "./stripeApiClient";

export interface StripeProduct {
  id: string;
  name: string;
  description?: string;
  [key: string]: unknown;
}

export interface StripePrice {
  id: string;
  product: string;
  unit_amount: number;
  currency: string;
  active: boolean;
  type: "one_time" | "recurring";
  [key: string]: unknown;
}

export interface StripeCustomer {
  id: string;
  email: string;
  name?: string;
  [key: string]: unknown;
}

export interface StripeSubscription {
  id: string;
  customer: string;
  status: string;
  items: unknown[];
  [key: string]: unknown;
}

// Helper to get a cookie value by name
function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : undefined;
}

// Set up interceptor for admin authentication
stripeApiClient.interceptors.request.use((config) => {
  const adminToken = getCookie("admin_token");
  if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  }
  return config;
});

export interface StripeInvoice {
  id: string;
  number: string;
  amount_paid: number;
  amount_due: number;
  currency: string;
  status: string;
  created: number;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
  [key: string]: unknown;
}

export interface PromoCodeValidation {
  valid: boolean;
  promotionCode?: StripePromotionCode;
  coupon?: StripeCoupon;
  discountType?: "percentage" | "amount";
  discountValue?: number;
  currency?: string;
  duration?: string;
  durationInMonths?: number;
}

export interface StripePromotionCode {
  id: string;
  code: string;
  active: boolean;
  coupon: StripeCoupon;
  expires_at?: number;
  max_redemptions?: number;
  times_redeemed: number;
  [key: string]: unknown;
}

export interface StripeCoupon {
  id: string;
  percent_off?: number;
  amount_off?: number;
  currency?: string;
  duration: "forever" | "once" | "repeating";
  duration_in_months?: number;
  applies_to?: {
    products?: string[];
  };
  [key: string]: unknown;
}

export interface CheckoutSessionResponse {
  url: string;
  sessionId: string;
}

export interface CreatePromotionCodeRequest {
  coupon: string;
  code?: string;
  active?: boolean;
  expires_at?: number;
  max_redemptions?: number;
  restrictions?: {
    first_time_transaction?: boolean;
    minimum_amount?: number;
    minimum_amount_currency?: string;
  };
}

const stripeService = {
  getProducts: async (): Promise<StripeProduct[]> => {
    const res = await stripeApiClient.get("/api/products");
    return res.data?.data?.data || [];
  },
  getPrices: async (): Promise<StripePrice[]> => {
    const res = await stripeApiClient.get("/api/prices");
    return res.data?.data?.data || [];
  },
  getPricesForProduct: async (productId: string): Promise<StripePrice[]> => {
    const res = await stripeApiClient.get(`/api/products/${productId}/prices`);
    return res.data?.data?.data || [];
  },
  createCustomer: async (data: {
    email: string;
    name?: string;
  }): Promise<StripeCustomer> => {
    const res = await stripeApiClient.post("/api/customers", data);
    return res.data?.data;
  },
  getCustomer: async (id: string): Promise<StripeCustomer> => {
    const res = await stripeApiClient.get(`/api/customers/${id}`);
    return res.data?.data;
  },
  createSubscription: async (data: {
    customer: string;
    items: { price: string }[];
  }): Promise<StripeSubscription> => {
    const res = await stripeApiClient.post("/api/subscriptions", data);
    return res.data?.data;
  },
  getSubscription: async (id: string): Promise<StripeSubscription> => {
    const res = await stripeApiClient.get(`/api/subscriptions/${id}`);
    return res.data?.data;
  },
  cancelSubscription: async (id: string): Promise<StripeSubscription> => {
    const res = await stripeApiClient.post(`/api/subscriptions/${id}/cancel`);
    return res.data?.data;
  },

  // Customer Portal
  createCustomerPortalSession: async (
    customerId: string,
    returnUrl?: string,
  ): Promise<{ url: string }> => {
    const res = await stripeApiClient.post("/api/customer-portal", {
      customerId,
      returnUrl,
    });
    return res.data?.data;
  },

  // Invoices
  getCustomerInvoices: async (
    customerId: string,
    limit = 10,
  ): Promise<StripeInvoice[]> => {
    const res = await stripeApiClient.get(
      `/api/customers/${customerId}/invoices?limit=${limit}`,
    );
    return res.data?.data || [];
  },

  getInvoicePdf: async (invoiceId: string): Promise<{ pdf_url: string }> => {
    const res = await stripeApiClient.get(`/api/invoices/${invoiceId}/pdf`);
    return res.data?.data;
  },

  // Promo codes
  validatePromoCode: async (
    promoCode: string,
    priceId: string,
  ): Promise<PromoCodeValidation> => {
    try {
      const res = await stripeApiClient.post("/api/validate-promo-code", {
        promoCode,
        priceId,
      });
      return res.data?.data;
    } catch (error: any) {
      // Extract specific error message from API response
      let errorMessage = "Code promo invalide ou expiré";

      if (error.message) {
        // Remove the "Stripe validatePromoCode error: " prefix if present
        errorMessage = error.message.replace(
          /^Stripe validatePromoCode error:\s*/,
          "",
        );
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      // Throw a new error with the cleaned message
      const cleanError = new Error(errorMessage);
      cleanError.name = "PromoCodeValidationError";
      throw cleanError;
    }
  },

  // Enhanced checkout session creation with promo code support
  createCheckoutSession: async (data: {
    priceId: string;
    promoCode?: string;
    allowPromotionCodes?: boolean;
  }): Promise<CheckoutSessionResponse> => {
    const res = await stripeApiClient.post("/api/checkout-session", data);
    return res.data;
  },

  // Checkout one-shot d'un pack de crédits IA (mode payment côté api-stripe)
  createAiCreditCheckout: async (data: {
    packId: string;
  }): Promise<CheckoutSessionResponse> => {
    const res = await stripeApiClient.post("/api/ai-credits/checkout", data);
    return res.data;
  },

  // Preview proration for subscription upgrade
  previewUpgradeProration: async (data: {
    subscriptionId: string;
    newPriceId: string;
  }): Promise<{
    subscription_id: string;
    new_price_id: string;
    current_plan: {
      price_id: string;
      amount: number;
      currency: string;
    };
    new_plan: {
      price_id: string;
      amount: number;
      currency: string;
    };
    billing_period: {
      start: number;
      end: number;
      total_days: number;
      used_days: number;
      remaining_days: number;
      next_billing_date: string;
    };
    proration_preview: {
      amount_due: number;
      credit_applied: number;
      currency: string;
      lines: any[];
      explanation: {
        immediate_charge: boolean;
        next_full_charge_date: string;
        next_full_charge_amount: number;
        billing_explanation: string;
      };
    };
  }> => {
    try {
      const res = await stripeApiClient.post(
        "/api/preview-upgrade-proration",
        data,
      );
      return res.data.data;
    } catch (error: any) {
      // Extract specific error message from API response
      let errorMessage = "Erreur lors de la prévisualisation de la proration";

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      // Throw a new error with the cleaned message
      const cleanError = new Error(errorMessage);
      cleanError.name = "ProrationPreviewError";
      throw cleanError;
    }
  },

  // Simple subscription upgrade with Stripe's built-in proration
  upgradeSubscriptionSimple: async (data: {
    subscriptionId: string;
    newPriceId: string;
  }): Promise<{
    subscription: StripeSubscription;
    proration_preview: {
      amount_due: number;
      amount_paid: number;
      currency: string;
      lines: any[];
    };
  }> => {
    try {
      const res = await stripeApiClient.post(
        "/api/upgrade-subscription-simple",
        data,
      );
      return res.data.data;
    } catch (error: any) {
      // Extract specific error message from API response
      let errorMessage = "Erreur lors de la mise à niveau de l'abonnement";

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      // Throw a new error with the cleaned message
      const cleanError = new Error(errorMessage);
      cleanError.name = "SubscriptionUpgradeError";
      throw cleanError;
    }
  },

  // Admin only - Promotion codes management
  listPromotionCodes: async (params?: {
    active?: boolean;
    limit?: number;
    starting_after?: string;
  }): Promise<StripePromotionCode[]> => {
    const queryParams = new URLSearchParams();
    if (params?.active !== undefined)
      queryParams.append("active", params.active.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.starting_after)
      queryParams.append("starting_after", params.starting_after);

    const res = await stripeApiClient.get(
      `/api/promotion-codes?${queryParams}`,
    );
    return res.data?.data?.data || [];
  },

  createPromotionCode: async (
    data: CreatePromotionCodeRequest,
  ): Promise<StripePromotionCode> => {
    const res = await stripeApiClient.post("/api/promotion-codes", data);
    return res.data?.data;
  },
};

export default stripeService;
